import { TransactionStatus } from '@cpce/database/enums';
import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { sumDecimals } from '../utils/decimal.utils.js';
import { zodMoney } from './decimal.types.js';
import { FinancialAccountTypeSchema } from './financial-accounts.types.js';
export const generalLedgerRowSchema = z.object({
    JournalCode: z.string(),
    JournalLib: z.string(),
    EcritureNum: z.string(),
    EcritureDate: z.string(),
    CompteNum: z.string(),
    CompteLib: z.string(),
    CompAuxNum: z.string(),
    CompAuxLib: z.string(),
    PieceRef: z.string(),
    PieceDate: z.string(),
    EcritureLib: z.string(),
    Debit: z.string(),
    Credit: z.string(),
    // Trailing columns (lettrage, validation, currency): many exports drop the final separators
    // when they are empty, so these fields vanish when parsing line by line. Tolerated as absent.
    EcritureLet: z.string().default(''),
    DateLet: z.string().default(''),
    ValidDate: z.string().default(''),
    Montantdevise: z.string().optional(),
    Idevise: z.string().optional(),
});
const importMovementDataSchema = z.object({
    name: z.string().min(1, 'Le libellé est requis'),
    amount: zodMoney().refine((value) => !value.isZero(), {
        message: 'Une ligne sans débit ni crédit ne peut pas être importée',
    }),
    financialAccountCode: z.string().min(1, 'Le code du compte financier est requis'),
    transactionType: z.enum(['debit', 'credit']),
    // FEC lettrage: code (`EcritureLet`) and date (`DateLet`). Movements sharing a code on the same
    // third-party account are matched at import, so the lettrage done in the previous software is
    // kept. `DateLet` disambiguates recycled codes.
    lettering: z.string().optional(),
    letteringDate: z.string().optional(),
});
const importMovementsDataSchema = z
    .array(importMovementDataSchema)
    .refine((movements) => sumDecimals(movements.map((movement) => movement.amount)).gt(0), {
    message: "Le total de l'écriture doit être supérieur à 0",
});
const importTransactionDataSchema = z
    .object({
    journalCode: z.string().min(1, 'Le code du journal est requis'),
    description: z.string().min(1, 'Le libellé est requis'),
    date: zDateField('La date est requise'),
    movements: importMovementsDataSchema,
    status: z.enum(TransactionStatus).optional(),
})
    .superRefine((data, ctx) => {
    const { movements } = data;
    const hasDebit = movements.some((m) => m.transactionType === 'debit');
    const hasCredit = movements.some((m) => m.transactionType === 'credit');
    if (!hasDebit || !hasCredit) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['movements'],
            message: 'Au moins un crédit et un débit sont requis',
        });
    }
    const totalDebit = sumDecimals(movements.filter((m) => m.transactionType === 'debit').map((m) => m.amount));
    const totalCredit = sumDecimals(movements.filter((m) => m.transactionType === 'credit').map((m) => m.amount));
    if (!totalDebit.equals(totalCredit)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['movements'],
            message: 'Le total des débits doit être égal au total des crédits',
        });
    }
});
const journalToCreateSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
});
const financialAccountToCreateSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    accountType: FinancialAccountTypeSchema,
});
export const createManyTransactionsSchema = z.object({
    accountingPeriodId: z.number({ message: "L'exercice est requis" }).int(),
    journalsToCreate: z.array(journalToCreateSchema),
    financialAccountsToCreate: z.array(financialAccountToCreateSchema),
    transactions: z.array(importTransactionDataSchema),
    filename: z.string().optional(),
});
